import { ReactNode, useCallback, useRef, useState } from "react";

import { getRaffleComplianceDisclaimer } from "@simplyk/common";

import { RaffleComplianceDisclaimerDialog } from "../RaffleComplianceDisclaimerDialog";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { trpc } from "@/common/helpers/trpc";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";

interface RaffleComplianceGate {
  gateRaffleCreation: (onProceed: () => Promise<void>) => boolean;
  raffleComplianceDisclaimerDialog: ReactNode;
}

export const useRaffleComplianceGate = (): RaffleComplianceGate => {
  const { organization } = useCurrentUserContext();
  const { logAmplitudeEvent } = useAmplitude();
  const { mutateAsync: createRaffleComplianceAttestation } = trpc.createRaffleComplianceAttestation.useMutation();

  const disclaimer = getRaffleComplianceDisclaimer(organization?.country, organization?.region);

  const [isOpen, setIsOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const onProceedRef = useRef<(() => Promise<void>) | null>(null);

  const gateRaffleCreation = useCallback(
    (onProceed: () => Promise<void>): boolean => {
      if (!disclaimer) {
        return false;
      }

      onProceedRef.current = onProceed;
      setIsOpen(true);
      logAmplitudeEvent(AmplitudeEvents.RaffleDisclaimerShown, {
        org_country: organization?.country,
        org_state: disclaimer.regionCode ?? organization?.region ?? "unknown",
        disclaimer_variant: disclaimer.variant,
      });
      return true;
    },
    [disclaimer, logAmplitudeEvent, organization?.country, organization?.region]
  );

  const handleConfirm = useCallback(async () => {
    if (!disclaimer) {
      return;
    }

    setIsConfirming(true);
    await createRaffleComplianceAttestation().catch(() => undefined);

    logAmplitudeEvent(AmplitudeEvents.RaffleDisclaimerAcknowledged, {
      org_country: organization?.country,
      org_state: disclaimer.regionCode ?? organization?.region ?? "unknown",
      disclaimer_variant: disclaimer.variant,
    });

    try {
      await onProceedRef.current?.();
      setIsOpen(false);
    } finally {
      setIsConfirming(false);
    }
  }, [createRaffleComplianceAttestation, disclaimer, logAmplitudeEvent, organization?.country, organization?.region]);

  const handleBack = useCallback(() => {
    if (isConfirming) {
      return;
    }
    setIsOpen(false);
  }, [isConfirming]);

  const raffleComplianceDisclaimerDialog = disclaimer ? (
    <RaffleComplianceDisclaimerDialog
      open={isOpen}
      variant={disclaimer.variant}
      learnMoreUrl={disclaimer.learnMoreUrl}
      isConfirming={isConfirming}
      onConfirm={handleConfirm}
      onBack={handleBack}
    />
  ) : null;

  return { gateRaffleCreation, raffleComplianceDisclaimerDialog };
};
